import { shouldUseNativeSanitizer } from "./shouldUseNativeSanitizer";
import { getSanitizerConfigAdapter } from "./adapters/SanitizerConfigAdapter";
import { postProcessUris } from "./adapters/uriPostProcessor";
import { getTrustedHTML } from "./getTrustedHTML";
import { SanitizerReturnType } from "./sanitizerReturnType";
import type { OwaSanitizerConfig } from "./owaSanitizerConfig";

/**
 * Sanitizes HTML and sets it directly on the target element.
 *
 * This uses `Element.setHTML()` directly — no temp elements, no string round-trip.
 * When the native path is off, falls back to `getTrustedHTML` + innerHTML assignment
 * that uses SanitizerReturnType.TrustedHTML so DOMPurify returns a TrustedHTML
 * object when Trusted Types are available, allowing the innerHTML assignment
 * without triggering the default TT policy (which would double-sanitize with
 * a stricter baseline config).
 */
export function setTrustedHTML(target: Element, html: string, extendedConfig: OwaSanitizerConfig = {}, optionalCacheID?: string): void {
    if (!html) {
        // If html is not defined, default to empty string to avoid setHTML returning null or unexpected behavior.
        html = "";
    }
    
    const useNative = shouldUseNativeSanitizer();
    
    if (useNative) {
        const sanitizer = getSanitizerConfigAdapter().toNativeSanitizerConfig(extendedConfig);
        target.setHTML(html, { sanitizer });
        postProcessUris(target, extendedConfig.allowUnknownProtocols);
    }
    else {
        /* eslint-disable-next-line @microsoft/sdl/no-inner-html --
         * The HTML is sanitized by DOMPurify via getTrustedHTML. When TT is available,
         * the returned value is a TrustedHTML object, so this assignment is safe. */
        target.innerHTML = getTrustedHTML(html, SanitizerReturnType.TrustedHTML, extendedConfig, optionalCacheID);
    }
}
